import React, { useState, useEffect, useMemo } from 'react';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import PF2MTable from '~/components/PF2MTable';
import Footer from '~/pages/Manager/Footer';
import { useTranslation } from 'react-i18next';
import styles from '../styles';

import {
  getEquipmentTypes,
  getEquipmentsGroups,
  getCodes,
  getCodeTypes,
} from '~/store/manager/actions';

import {
  getWorkHourValues,
  exportWorkHourValues,
  importWorkHourValues,
} from '~/store/contracts/actions';

import Header from '~/pages/Manager/Header';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';

const WorkHourValueTab = ({
  classes,
  workHourValues,
  equipmentsGroups,
  equipmentTypes,
  codes,
  getWorkHourValues: getAll,
  getEquipmentTypes: loadEquipmentTypes,
  getEquipmentsGroups: loadEquipmentsGroups,
  getCodes: loadCodes,
  exportWorkHourValues: exportAction,
  importWorkHourValues: importAction,
  codeTypes,
  getCodeTypes: loadCodeTypes,
}) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [equipmentType, setEquipmentType] = useState(0);
  const [equipmentGroup, setEquipmentGroup] = useState(0);
  const [code, setCode] = useState(0);
  const [codeType, setCodeType] = useState(-1);
  const { t: translate } = useTranslation();

  useEffect(() => {
    async function fetchData() {
      getAll();
      loadEquipmentsGroups();
      loadEquipmentTypes();
      loadCodeTypes();
      await loadCodes();
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, getAll, loadEquipmentsGroups, loadEquipmentTypes, loadCodes, loadCodeTypes]);

  const columns = [
    { field: 'equipment_type_name', title: translate('common:EquipmentType'), width: 200 },
    { field: 'equip_group_name', title: translate('common:EquipmentGroup'), width: 200 },
    { field: 'code_name', title: translate('common:Code') },
    { field: 'value', title: translate('common:ValueMoneyTime') },
  ];

  const filteredValues = useMemo(() => workHourValues
    .filter(e => equipmentType === 0 || e.id_equipment_type === equipmentType)
    .filter(e => equipmentGroup === 0 || e.id_equip_group === equipmentGroup)
    .filter(e => code === 0 || e.id_code === code), [
    workHourValues,
    equipmentType,
    equipmentGroup,
    code,
  ]);

  const getRowKey = item => `${parseInt(Math.random() * 10000, 10)}_${item.id_code}_${item.id_equipment_type}_${item.id_equip_group}`;

  const handleExport = (filters = {}, argument) => {
    exportAction(
      {
        ...filters,
        codeType,
      },
      argument,
    );
  };

  const handleImport = async (...args) => {
    await importAction(...args);
    getAll();
  };

  return (
    <div className={classes.tabContainer}>
      <div style={{ display: 'flex', alignItems: 'baseline', margin: 10 }}>
        <p className={classes.labelTypeSelector}>
          {translate('common:CodeType').toUpperCase()}
          :
        </p>
        <PF2MSearchSelectOutlined
          value={codeType}
          onChange={(e) => {
            setCodeType(e.target.value);
          }}
          placeholder={null}
          clearedValue={-1}
        >
          {[{ value: -1, label: translate('common:Select') }]
            .concat(
              codeTypes.map(e => ({
                value: e.id,
                label: translate(`common:${e.name}`),
              })),
            )}
        </PF2MSearchSelectOutlined>
      </div>
      <Header
        importAction={handleImport}
        exportAction={handleExport}
        disableExport={codeType === -1}
      />
      <hr />
      <div className={classes.containerTypeSelector}>
        <div style={{ display: 'flex', alignItems: 'baseline', margin: 10 }}>
          <p className={classes.labelTypeSelector}>
            {translate('common:EquipmentType').toUpperCase()}
            :
          </p>
          <PF2MSearchSelectOutlined
            value={equipmentType}
            onChange={(e) => {
              setEquipmentType(e.target.value);
              setEquipmentGroup(0);
              setCode(0);
            }}
            placeholder={null}
          >
            {[{ value: 0, label: translate('common:Select') }]
              .concat(...equipmentTypes
                .map(e => ({ value: e.id, label: e.name })))}
          </PF2MSearchSelectOutlined>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', margin: 10 }}>
          <p className={classes.labelTypeSelector}>
            {translate('common:EquipmentGroup').toUpperCase()}
            :
          </p>
          <PF2MSearchSelectOutlined
            value={equipmentGroup}
            onChange={e => setEquipmentGroup(e.target.value)}
            placeholder={null}
          >
            {[{ value: 0, label: translate('common:Select') }]
              .concat(...equipmentsGroups
                .filter(e => equipmentType === 0 || e.id_equipament === equipmentType)
                .map(e => ({ value: e.id, label: e.name })))}
          </PF2MSearchSelectOutlined>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', margin: 10 }}>
          <p className={classes.labelTypeSelector}>
            {translate('common:Code').toUpperCase()}
            :
          </p>
          <PF2MSearchSelectOutlined
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder={null}
          >
            {[{ value: 0, label: translate('common:Select') }]
              .concat(...codes
                .filter(c => c.id_equip === equipmentType)
                .map(e => ({ value: e.id, label: e.name })))}
          </PF2MSearchSelectOutlined>
        </div>
      </div>
      <PF2MTable
        data={filteredValues}
        keyExtractor={getRowKey}
        columns={columns}
        className={classes.grid}
      />
      <Footer />
    </div>
  );
};

WorkHourValueTab.propTypes = {
  classes: PropTypes.object.isRequired,
  workHourValues: PropTypes.array,
  equipmentTypes: PropTypes.array,
  equipmentsGroups: PropTypes.array,
  codes: PropTypes.array,
  getEquipmentTypes: PropTypes.func,
  getEquipmentsGroups: PropTypes.func,
  getWorkHourValues: PropTypes.func,
  exportWorkHourValues: PropTypes.func,
  importWorkHourValues: PropTypes.func,
  getCodes: PropTypes.func,
  codeTypes: PropTypes.array,
  getCodeTypes: PropTypes.func,
};

WorkHourValueTab.defaultProps = {
  workHourValues: [],
  equipmentTypes: [],
  equipmentsGroups: [],
  codes: [],
  getEquipmentTypes: null,
  getEquipmentsGroups: null,
  getWorkHourValues: null,
  exportWorkHourValues: null,
  importWorkHourValues: null,
  getCodes: null,
  codeTypes: [],
  getCodeTypes: null,
};

const mapStateToProps = state => ({
  workHourValues: state.contracts.workHourValues,
  equipmentTypes: state.manager.equipmentTypes,
  equipmentsGroups: state.manager.equipmentsGroups,
  codes: state.manager.codes,
  codeTypes: state.manager.codeTypes,
});

const mapDispatchToProps = {
  getEquipmentTypes,
  getEquipmentsGroups,
  getWorkHourValues,
  exportWorkHourValues,
  importWorkHourValues,
  getCodes,
  getCodeTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(WorkHourValueTab));
